import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, MapPin, Phone, Mail } from "lucide-react";
import { SITE } from "@/data/site";
import { api, asError } from "@/api/client";
import Reveal from "@/components/Reveal";

const ENQ_TYPES = ["Consumer Support", "Bulk Orders", "Retail Distribution", "Franchise Enquiry", "Corporate Meals", "Media Enquiry"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", type: "Consumer Support", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Name, email and message are required."); return; }
    setSubmitting(true);
    try {
      const subjectLine = [form.type, form.subject].filter(Boolean).join(" · ");
      await api.post("/contact", {
        name: form.name,
        email: form.email,
        subject: subjectLine,
        message: `${form.message}${form.phone ? "\n\nPhone: " + form.phone : ""}`,
      });
      toast.success("Thank you — we'll respond within 24 hours.");
      setForm({ name: "", email: "", phone: "", subject: "", type: "Consumer Support", message: "" });
    } catch (err) {
      toast.error(asError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact" className="bg-[var(--bg)]">
      {/* HERO */}
      <section className="pt-[110px] md:pt-[120px] pb-10 bg-[var(--bg)]">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10 text-center max-w-3xl">
          <Reveal>
            <div className="overline overline-green">Get in touch</div>
            <h1 className="font-serif-display text-4xl md:text-6xl mt-4 leading-tight text-[var(--ink)] font-semibold">
              We're here <span className="text-[var(--green)]">to help.</span>
            </h1>
            <p className="mt-5 max-w-xl mx-auto text-base md:text-lg text-[var(--ink-2)] leading-relaxed">
              Questions, partnerships, feedback or just a kind word — write to us. Every message
              is read by a human, usually within a day.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Info column */}
          <Reveal className="lg:col-span-4 space-y-5">
            <InfoCard icon={MapPin} title="Registered Office" body={SITE.contact.address} />
            <InfoCard icon={Phone} title="Call us" body={
              <>
                <div><a href={`tel:${SITE.contact.phone}`} data-testid="contact-phone" className="link-underline">{SITE.contact.phone}</a></div>
                <div className="mt-1"><a href={`tel:${SITE.contact.support}`} data-testid="contact-support" className="link-underline">{SITE.contact.support} · Support</a></div>
              </>
            } />
            <InfoCard icon={Mail} title="Email us" body={
              <a href={`mailto:${SITE.contact.email}`} data-testid="contact-email" className="link-underline">{SITE.contact.email}</a>
            } />

            <div className="card-soft p-6">
              <div className="overline overline-green">Quick categories</div>
              <div className="mt-4 flex flex-wrap gap-2">
                {ENQ_TYPES.map((t) => (
                  <button
                    key={t}
                    data-testid={`enq-type-${t.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => setForm({ ...form, type: t })}
                    className={`text-[0.7rem] uppercase tracking-[0.18em] font-semibold px-3 py-2 rounded-full border transition ${
                      form.type === t
                        ? "bg-[var(--green)] text-white border-[var(--green)]"
                        : "bg-white text-[var(--ink-2)] border-[var(--line)] hover:border-[var(--green)] hover:text-[var(--green)]"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
          </Reveal>

          {/* Form */}
          <Reveal delay={0.1} className="lg:col-span-8">
            <form data-testid="contact-form" onSubmit={submit} className="card-soft p-6 md:p-9 grid grid-cols-1 md:grid-cols-2 gap-4">
              <FieldL testId="contact-name" required label="Full name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
              <FieldL testId="contact-form-email" required type="email" label="Email *" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
              <FieldL testId="contact-form-phone" label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
              <FieldL testId="contact-subject" label="Subject" value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} />
              <div className="md:col-span-2 flex flex-col">
                <label className="kbd text-[var(--ink-3)] mb-2">Enquiry type</label>
                <select
                  data-testid="contact-type"
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                  className="bg-white border border-[var(--line)] rounded-md px-4 py-2.5 text-sm focus:border-[var(--green)] focus:outline-none"
                >
                  {ENQ_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div className="md:col-span-2 flex flex-col">
                <label className="kbd text-[var(--ink-3)] mb-2">Message *</label>
                <textarea
                  data-testid="contact-message"
                  rows={6}
                  required
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Tell us how we can help."
                  className="bg-white border border-[var(--line)] rounded-md px-4 py-2.5 text-sm focus:border-[var(--green)] focus:outline-none"
                />
              </div>
              <div className="md:col-span-2">
                <button data-testid="contact-submit" type="submit" className="btn-primary">Send message <ArrowRight size={14} /></button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

function InfoCard({ icon: Icon, title, body }) {
  return (
    <div className="card-soft p-6 flex gap-4">
      <div className="w-11 h-11 rounded-full bg-[var(--bg-cream)] flex items-center justify-center shrink-0">
        <Icon size={18} className="text-[var(--green)]" />
      </div>
      <div>
        <div className="overline overline-green">{title}</div>
        <div className="mt-2 text-[var(--ink)] text-sm leading-relaxed">{body}</div>
      </div>
    </div>
  );
}

function FieldL({ testId, label, value, onChange, required = false, type = "text" }) {
  return (
    <div className="flex flex-col">
      <label className="kbd text-[var(--ink-3)] mb-2">{label}</label>
      <input
        data-testid={testId}
        required={required}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-white border border-[var(--line)] rounded-md px-4 py-2.5 text-sm focus:border-[var(--green)] focus:outline-none"
      />
    </div>
  );
}
